import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Typography,
  Tag,
  Modal,
  message,
  Button,
  Alert,
} from "antd";
import {
  PlusOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import { trackFeatureUsage } from "../lib/posthog";
import { AdminNavBar } from "../components/AdminNavBar";
import InvoiceList from "../components/invoices/InvoiceList";
import InvoiceForm from "../components/invoices/InvoiceForm";
import InvoicePreview from "../components/invoices/InvoicePreview";
import RuleSelectionInfo from "../components/invoices/RuleSelectionInfo";
import { Invoice } from "../types/invoice";
import { fetchInvoiceById } from "../services/invoiceService";
import { supabase } from "../lib/supabase";
import { Booking } from "../types/booking";
import { SupabaseClient } from "@supabase/supabase-js";
import {
  createMoneybirdInvoice,
  updateMoneybirdInvoice,
  syncAllMoneybirdStatus,
} from "../services/moneybirdService";

const { Title } = Typography;

// Type cast supabase to avoid TypeScript errors
const typedSupabase = supabase as unknown as SupabaseClient;

const InvoicesAdmin: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  /** Only for create-from-booking deep links — never reuse edit/preview invoice state. */
  const [createInitialBookingId, setCreateInitialBookingId] = useState<number | undefined>();
  /** Bumps on every create-open so InvoiceForm always remounts blank. */
  const [createFormKey, setCreateFormKey] = useState(0);
  const [previewInvoice, setPreviewInvoice] = useState<Invoice | null>(null);
  const [previewBooking, setPreviewBooking] = useState<Booking | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [bookings, setBookings] = useState<Booking[]>([]);

  // Moneybird state
  const [moneybirdLoading, setMoneybirdLoading] = useState<{
    create: boolean;
    update: boolean;
    sync: boolean;
    syncAll: boolean;
  }>({
    create: false,
    update: false,
    sync: false,
    syncAll: false,
  });

  // Track page view on component mount
  useEffect(() => {
    trackFeatureUsage("invoices_admin");
  }, []);

  // Handle URL parameters for creating invoice from booking or showing specific invoice
  useEffect(() => {
    const createFromBookingId = searchParams.get("create_from_booking");
    const invoiceId = searchParams.get("invoice_id");

    if (createFromBookingId) {
      // Auto-open create invoice modal with specific booking
      const bookingId = parseInt(createFromBookingId, 10);
      if (bookingId && !isNaN(bookingId)) {
        console.log("Creating invoice for booking ID:", bookingId);

        // Fetch bookings first, then proceed with invoice creation
        fetchBookings().then(() => {
          openCreateModal(bookingId);
        });

        // Clear the URL parameter
        setSearchParams({});
      }
    } else if (invoiceId) {
      // Auto-show specific invoice (could implement filtering in InvoiceList later)
      console.log("Showing invoice ID:", invoiceId);
      message.info(`Showing invoice #${invoiceId}`);
      // Clear the URL parameter
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  // Fetch bookings from Supabase
  const fetchBookings = async () => {
    try {
      const { data, error } = await typedSupabase
        .from("bookings")
        .select(
          `
          id,
          reference,
          status,
          created_at,
          transport_type_info:booking_transport_types(*),
          booking_type_info:bookings_type(*),
          company_info:company(*),
          origin_port_info:booking_origin_port(*),
          destination_port_info:booking_destination_port(*),
          booking_incoterm_info:booking_incoterm(*),
          container_info:containers(
            id,
            reference,
            origin_port:booking_origin_port(*),
            destination_port:booking_destination_port(*)
          ),
          product_info:booking_product(*)
        `
        )
        .order("created_at", { ascending: false })
        .limit(100);

      if (error) throw error;
      if (data) setBookings(data as unknown as Booking[]);
    } catch (error) {
      console.error("Error fetching bookings:", error);
      message.error("Failed to load bookings");
    }
  };

  const closeCreateModal = () => {
    setCreateModalVisible(false);
    setCreateInitialBookingId(undefined);
  };

  // Always open a blank create wizard (optional pre-selected booking for deep links)
  const openCreateModal = (bookingId?: number) => {
    setCreateInitialBookingId(bookingId);
    setCreateFormKey((k) => k + 1);
    setCreateModalVisible(true);
  };

  const showCreateModal = () => openCreateModal();

  // Show invoice view modal (currently unused by InvoiceList component)
  /* const showViewModal = async (invoice: Invoice) => {
    try {
      // Fetch full invoice details including lines
      const { data, error } = await fetchInvoiceById(invoice.id!);

      if (error) {
        throw error;
      }

      if (data) {
        setSelectedInvoice(data);
        setViewModalVisible(true);
      }
    } catch (error) {
      console.error("Error fetching invoice details:", error);
      message.error("Failed to load invoice details");
    }
  }; */

  // Show invoice edit modal
  const showEditModal = async (invoice: Invoice) => {
    try {
      // Fetch full invoice details including lines
      const { data, error } = await fetchInvoiceById(invoice.id!);

      if (error) {
        throw error;
      }

      if (data) {
        setSelectedInvoice(data);
        setEditModalVisible(true);
      }
    } catch (error) {
      console.error("Error fetching invoice details:", error);
      message.error("Failed to load invoice details");
    }
  };

  // Show invoice preview modal
  const showPreviewModal = async (invoice: Invoice) => {
    try {
      // Fetch full invoice details including lines
      const { data, error } = await fetchInvoiceById(invoice.id!);

      if (error) {
        throw error;
      }

      if (data) {
        setPreviewInvoice(data);

        // Find the booking associated with this invoice
        const booking = bookings.find((b) => b.id === data.booking_id);
        if (booking) {
          setPreviewBooking(booking);
        } else {
          // If booking not in current list, fetch it specifically
          const { data: bookingData, error: bookingError } = await typedSupabase
            .from("bookings")
            .select(
              `
              *,
              transport_type_info:booking_transport_types(*),
              booking_type_info:bookings_type(*),
              company_info:company(*),
              origin_port_info:booking_origin_port(*),
              destination_port_info:booking_destination_port(*),
              booking_incoterm_info:booking_incoterm(*),
              container_info:containers(
                id,
                reference,
                etd,
                eta,
                origin_port:booking_origin_port(*),
                destination_port:booking_destination_port(*)
              ),
              product_info:booking_product(*)
            `
            )
            .eq("id", data.booking_id)
            .single();

          if (bookingError) {
            console.error("Error fetching booking:", bookingError);
            message.warning("Could not load booking details for preview");
            setPreviewBooking(null);
          } else {
            setPreviewBooking(bookingData as unknown as Booking);
          }
        }

        setPreviewModalVisible(true);
      }
    } catch (error) {
      console.error("Error fetching invoice details:", error);
      message.error("Failed to load invoice details");
    }
  };

  // Handle invoice creation success
  const handleCreateSuccess = () => {
    closeCreateModal();
    setRefreshTrigger((prev) => prev + 1);
  };

  // Handle invoice edit success
  const handleEditSuccess = () => {
    setEditModalVisible(false);
    setSelectedInvoice(null);
    setRefreshTrigger((prev) => prev + 1);
  };

  // Refresh the invoice list
  const handleRefresh = () => {
    setRefreshTrigger((prev) => prev + 1);
  };

  // Moneybird handlers
  const handleCreateMoneybirdInvoice = async (invoice: Invoice) => {
    if (!invoice.id) return;

    try {
      setMoneybirdLoading((prev) => ({ ...prev, create: true }));

      const { data, error } = await createMoneybirdInvoice(invoice.id);

      if (error) {
        message.error(`Failed to create Moneybird invoice: ${error}`);
        return;
      }

      if (data?.success) {
        message.success(
          data.message || "Invoice successfully created in Moneybird"
        );
        if (data.moneybird_invoice_url) {
          window.open(data.moneybird_invoice_url, "_blank");
        }
        handleRefresh();
      } else {
        message.error(data?.error || "Failed to create Moneybird invoice");
      }
    } catch (error) {
      console.error("Error creating Moneybird invoice:", error);
      message.error("An unexpected error occurred");
    } finally {
      setMoneybirdLoading((prev) => ({ ...prev, create: false }));
    }
  };

  const handleUpdateMoneybirdInvoice = async (invoice: Invoice) => {
    if (!invoice.id) return;

    try {
      setMoneybirdLoading((prev) => ({ ...prev, update: true }));

      const { data, error } = await updateMoneybirdInvoice(invoice.id);

      if (error) {
        message.error(`Failed to update Moneybird invoice: ${error}`);
        return;
      }

      if (data?.success) {
        message.success(
          data.message || "Invoice successfully updated in Moneybird"
        );
        if (data.moneybird_invoice_url) {
          window.open(data.moneybird_invoice_url, "_blank");
        }
        handleRefresh();
      } else {
        message.error(data?.error || "Failed to update Moneybird invoice");
      }
    } catch (error) {
      console.error("Error updating Moneybird invoice:", error);
      message.error("An unexpected error occurred");
    } finally {
      setMoneybirdLoading((prev) => ({ ...prev, update: false }));
    }
  };

  const handleSyncAllMoneybirdStatus = async () => {
    try {
      setMoneybirdLoading((prev) => ({ ...prev, syncAll: true }));

      const { data, error } = await syncAllMoneybirdStatus();

      if (error) {
        message.error(`Failed to sync all Moneybird statuses: ${error}`);
        return;
      }

      if (data?.success && data.results) {
        const { success, failed, updated } = data.results;
        message.success(
          `Sync completed: ${success} successful, ${updated} updated, ${failed} failed`
        );
        if (failed > 0 && data.results.errors.length > 0) {
          console.error("Sync errors:", data.results.errors);
        }
        handleRefresh();
      } else {
        message.error(data?.error || "Failed to sync all Moneybird statuses");
      }
    } catch (error) {
      console.error("Error syncing all Moneybird statuses:", error);
      message.error("An unexpected error occurred");
    } finally {
      setMoneybirdLoading((prev) => ({ ...prev, syncAll: false }));
    }
  };

  // Handle edit from preview modal
  const handleEditFromPreview = () => {
    if (previewInvoice) {
      setSelectedInvoice(previewInvoice);
      setPreviewModalVisible(false);
      setEditModalVisible(true);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <AdminNavBar />

      <Title level={2}>
        Invoices
        <Tag color="warning" style={{ marginLeft: 12, fontSize: 14 }}>
          Work in progress
        </Tag>
      </Title>

      <div
        style={{ marginBottom: 16, display: "flex", gap: 8, flexWrap: "wrap" }}
      >
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={showCreateModal}
        >
          Create Invoice
        </Button>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <Button
            type="default"
            icon={<SyncOutlined />}
            loading={moneybirdLoading.syncAll}
            onClick={handleSyncAllMoneybirdStatus}
          >
            Sync All Moneybird Status
          </Button>
        </div>
      </div>

      <InvoiceList
        onCreateClick={showCreateModal}
        onPreviewClick={showPreviewModal}
        onEditClick={showEditModal}
        onRefresh={handleRefresh}
        onCreateMoneybirdInvoice={handleCreateMoneybirdInvoice}
        onUpdateMoneybirdInvoice={handleUpdateMoneybirdInvoice}
        moneybirdLoading={moneybirdLoading}
        key={refreshTrigger}
      />

      {/* Create Invoice Modal */}
      <Modal
        title="Create New Invoice"
        open={createModalVisible}
        onCancel={closeCreateModal}
        footer={null}
        width={1600}
        style={{ top: 20 }}
        destroyOnClose
      >
        {createModalVisible && (
          <InvoiceForm
            key={`invoice-form-new-${createFormKey}`}
            onSuccess={handleCreateSuccess}
            onCancel={closeCreateModal}
            initialBookingId={createInitialBookingId}
          />
        )}
      </Modal>

      {/* View Invoice Modal */}
      <Modal
        title={`Invoice ${
          selectedInvoice?.id || ""
        } - Pricing Decision Analysis`}
        open={viewModalVisible}
        onCancel={() => setViewModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setViewModalVisible(false)}>
            Close
          </Button>,
        ]}
        width={1000}
      >
        {selectedInvoice &&
          (() => {
            // Find the booking for this invoice
            const booking = bookings.find(
              (b) => b.id === selectedInvoice.booking_id
            );
            const companyId = booking?.company_info
              ? Array.isArray(booking.company_info)
                ? booking.company_info[0]?.id
                : booking.company_info.id
              : undefined;

            return (
              <div>
                {booking ? (
                  <RuleSelectionInfo booking={booking} companyId={companyId} />
                ) : (
                  <Alert
                    type="warning"
                    message="Booking data not available"
                    description="Cannot display rule analysis without booking information."
                    showIcon
                  />
                )}
                {/* Future: Add other invoice details here */}
              </div>
            );
          })()}
      </Modal>

      {/* Edit Invoice Modal */}
      <Modal
        title={`Edit Invoice ${selectedInvoice?.id || ""}`}
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          setSelectedInvoice(null);
        }}
        footer={null}
        width={1600}
        destroyOnClose
      >
        {editModalVisible && selectedInvoice && (
          <InvoiceForm
            key={`invoice-form-edit-${selectedInvoice.id}`}
            onSuccess={handleEditSuccess}
            onCancel={() => {
              setEditModalVisible(false);
              setSelectedInvoice(null);
            }}
            editInvoice={selectedInvoice}
          />
        )}
      </Modal>

      {/* Preview Invoice Modal */}
      <Modal
        title={`Invoice Preview - ${previewInvoice?.id || ""}`}
        open={previewModalVisible}
        onCancel={() => setPreviewModalVisible(false)}
        footer={[
          <Button key="edit" type="primary" onClick={handleEditFromPreview}>
            Edit Invoice
          </Button>,
          <Button key="close" onClick={() => setPreviewModalVisible(false)}>
            Close
          </Button>,
        ]}
        width={800}
        style={{ top: 20 }}
      >
        {previewInvoice && (
          <InvoicePreview invoice={previewInvoice} booking={previewBooking} />
        )}
      </Modal>
    </div>
  );
};

export default InvoicesAdmin;
